import { Badge, BadgeProps } from '@chakra-ui/react'
import { formatChainName, getChainInfo } from '@/constants/chains'

interface ChainBadgeProps extends Omit<BadgeProps, 'colorScheme'> {
  chainId?: string | number | null
  chainCode?: string | null
  chainName?: string | null
  chainNumericId?: string | number | null
  isTestnet?: boolean | null
}

const CHAIN_DISPLAY_LABELS: Record<string, string> = {
  eth: 'ETHEREUM',
  ethereum: 'ETHEREUM',
  'ethereum-sepolia': 'ETH-SEPOLIA',
  base: 'BASE',
  'base-mainnet': 'BASE',
  bsc: 'BNB Smart Chain',
  'bnb-chain': 'BNB Smart Chain',
  'bnb-smart-chain': 'BNB Smart Chain',
  'bnb-chain-testnet': 'BNB Smart Chain Testnet',
  'bnb-smart-chain-testnet': 'BNB Smart Chain Testnet',
  polygon: 'POLYGON',
  'polygon-mainnet': 'POLYGON',
  tron: 'TRON',
  'tron-mainnet': 'TRON',
  solana: 'SOLANA',
  'solana-mainnet': 'SOLANA',
  'eth-sepolia': 'ETH-SEPOLIA',
  sepolia: 'ETH-SEPOLIA',
  'base-sepolia': 'BASE-SEPOLIA',
  'bsc-testnet': 'BNB Smart Chain Testnet',
  'polygon-amoy': 'POLYGON-AMOY',
  'polygon-amoy-testnet': 'POLYGON-AMOY',
  amoy: 'POLYGON-AMOY',
  'tron-nile': 'TRON-NILE',
  'tron-nile-testnet': 'TRON-NILE',
  nile: 'TRON-NILE',
  'solana-devnet': 'SOLANA-DEVNET',
  devnet: 'SOLANA-DEVNET',
}

const CHAIN_DISPLAY_MATCH_KEYS = Object.keys(CHAIN_DISPLAY_LABELS)
  .sort((left, right) => right.length - left.length)

const normalizeCandidate = (value: unknown): string | null => {
  if (value === null || value === undefined) {
    return null
  }
  const normalized = String(value).trim()
  return normalized.length > 0 ? normalized : null
}

const labelFromFallback = (
  chainName?: string | null,
  chainCode?: string | null,
  isTestnet?: boolean | null
): string => {
  const source = normalizeCandidate(chainCode) ?? normalizeCandidate(chainName)
  if (!source) {
    return 'UNKNOWN'
  }
  const normalized = source
    .toLowerCase()
    .replace(/\d+$/u, '')
    .replace(/[_\s]+/gu, '-')
    .replace(/-main$/u, '')
    .replace(/-mainnet$/u, '')
    .replace(/^-+|-+$/gu, '')

  const direct = CHAIN_DISPLAY_LABELS[normalized]
  if (direct) {
    return direct
  }

  const compact = normalized.replace(/-/gu, '')
  const matchedKey = CHAIN_DISPLAY_MATCH_KEYS.find(key =>
    compact.includes(key.replace(/-/gu, ''))
  )
  if (matchedKey) {
    return CHAIN_DISPLAY_LABELS[matchedKey]
  }

  const name = normalizeCandidate(chainName)
  if (name) {
    const upper = name.toUpperCase()
    if (isTestnet && !/(TESTNET|DEVNET|SEPOLIA|AMOY|NILE)/u.test(upper)) {
      return `${upper} TESTNET`
    }
    return upper
  }

  return source.toUpperCase()
}

const resolveChainIdentifier = ({
  chainId,
  chainCode,
  chainName,
  chainNumericId,
}: ChainBadgeProps): string | number | null => (
  normalizeCandidate(chainCode) ??
  normalizeCandidate(chainId) ??
  normalizeCandidate(chainNumericId) ??
  normalizeCandidate(chainName)
)

const resolveChainColors = (values: Array<string | number | null | undefined>) => {
  const normalized = values
    .map(value => normalizeCandidate(value))
    .filter((value): value is string => Boolean(value))
    .join(' ')
    .toLowerCase()

  if (normalized.includes('bsc') || normalized.includes('bnb')) {
    return { bg: '#F3BA2F', color: 'black' }
  }
  if (normalized.includes('base')) {
    return { bg: '#2056FD', color: 'white' }
  }
  if (normalized.includes('polygon') || normalized.includes('amoy')) {
    return { bg: '#6C5CE7', color: 'white' }
  }
  if (normalized.includes('tron') || normalized.includes('nile')) {
    return { bg: '#EF0027', color: 'white' }
  }
  if (normalized.includes('solana') || normalized.includes('devnet')) {
    return { bg: '#9945FF', color: 'white' }
  }
  if (normalized.includes('eth') || normalized.includes('ethereum') || normalized.includes('sepolia')) {
    return { bg: '#627EEA', color: 'white' }
  }
  return { bg: 'gray.500', color: 'white' }
}

export function ChainBadge({
  chainId,
  chainCode,
  chainName,
  chainNumericId,
  isTestnet,
  ...props
}: ChainBadgeProps) {
  const chainIdentifier = resolveChainIdentifier({ chainId, chainCode, chainName, chainNumericId })
  const chainInfo = getChainInfo(chainIdentifier)
  const registryLabel = formatChainName(chainIdentifier, 'short')
  const fallbackLabel = labelFromFallback(
    chainName,
    chainCode ?? (typeof chainIdentifier === 'string' ? chainIdentifier : null),
    isTestnet
  )
  const chainDisplayName = fallbackLabel !== 'UNKNOWN'
    ? fallbackLabel
    : registryLabel && registryLabel.toLowerCase() !== 'unknown'
      ? registryLabel
      : fallbackLabel
  const colors = resolveChainColors([
    chainInfo.prefix,
    chainInfo.chainId,
    chainInfo.name,
    chainInfo.shortName,
    chainIdentifier,
    chainCode,
    chainName,
  ])

  return (
    <Badge
      bg={colors.bg}
      color={colors.color}
      size="sm"
      px={2}
      py={1}
      borderRadius="md"
      fontWeight="medium"
      {...props}
    >
      {chainDisplayName}
    </Badge>
  )
}
